import './Modals.scss';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Input, Modal } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { Company, ModalProps } from '../../utils/types';
import {
  getErrorMessage,
  Notify,
  postData,
  updateData,
} from '../../utils/constants';

type CompanyModalProps = ModalProps & {
  company: Company | null;
  refreshData: (name: string) => Promise<void>;
};

const CompanyModal = ({
  isOpen,
  setIsOpen,
  company,
  refreshData,
}: CompanyModalProps) => {
  const [name, setName] = useState<string>('');

  const handleSubmit = useCallback(async () => {
    const isEdit = !!company?.id;
    const formData = {
      ...(isEdit ? { id: company.id } : {}),
      name,
    };

    const url = isEdit
      ? `${process.env.REACT_APP_API_URL}/companies/updateCompany/${company.id}`
      : `${process.env.REACT_APP_API_URL}/companies/createCompany`;

    try {
      isEdit ? await updateData(url, formData) : await postData(url, formData);
      Notify(
        'success',
        `Company ${isEdit ? 'updated' : 'created'} successfully!`,
      );
      setIsOpen(false);
      await refreshData(isEdit ? 'all' : 'companies');
    } catch (error) {
      console.error(`${isEdit ? 'PUT' : 'POST'} error:`, error);
      Notify(
        'error',
        getErrorMessage(
          error,
          `Oops! Something went wrong while ${isEdit ? 'updating' : 'creating'} company.`,
        ),
      );
    }
  }, [company, name, refreshData, setIsOpen]);

  useEffect(() => {
    if (!isOpen) {
      setName('');
    } else {
      setName(company?.name || '');
    }
  }, [isOpen, company]);

  return (
    <Modal
      open={isOpen}
      onCancel={() => setIsOpen(false)}
      title={company?.id ? 'Update company' : 'Add new company'}
      className="md"
      footer={[
        <Button key="cancel" onClick={() => setIsOpen(false)}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          icon={<CheckOutlined />}
          onClick={handleSubmit}
        >
          {company?.id ? 'Update company' : 'Create company'}
        </Button>,
      ]}
    >
      <Form layout="vertical">
        <Form.Item label="Name">
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CompanyModal;
